import React from "react";
import {FileSearchOutlined, ImportOutlined, InboxOutlined, SelectOutlined, WarningOutlined} from "@ant-design/icons";
import {Button, ButtonColors, ButtonVariants, Card, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SplitLayout from "../../../common/layouts/splitLayout/SplitLayout";
import EmptyState from "../../../common/emptyState/EmptyState";
import MemberSearch from "./MemberSearch";
import IntakeJobsTable from "./IntakeJobsTable";
import DocumentPreview from "./DocumentPreview";
import JobMetadataCard from "./JobMetadataCard";
import styles from "../../styles/document-audit.module.scss";

/**
 * Document Audit layout: member search + uploads table on the left, the
 * selected upload's preview/metadata rail on the right (stacks below the
 * table under 1024px — no modal). Purely presentational: every fetch,
 * selection and object-URL concern lives on the page controller.
 *
 * @param {Object} props
 * @param {string} props.memberQuery
 * @param {{clientId: string, name: string, email: string}[]} props.memberResults
 * @param {boolean} props.memberSearchLoading
 * @param {{clientId: string, name?: string, email?: string} | null} props.selectedMember
 * @param {Object[]} props.jobs
 * @param {boolean} props.jobsLoading
 * @param {{kind: string, title: string, detail: string} | null} props.jobsError
 * @param {string | null} props.selectedJobId
 * @param {{objectUrl: string | null, mimeType: string | null, loading: boolean, error: object | null}} props.preview
 * @param {boolean} props.memberSorLoading
 * @param {(term: string) => void} props.onSearchMembers
 * @param {(member: object) => void} props.onSelectMember
 * @param {(jobId: string) => void} props.onSelectJob
 * @param {() => void} props.onLoadMemberSor — "Load this member's SOR into the composer"
 * @param {(text: string, label: string) => void} props.onCopyText
 */
function DocumentAuditPanel({
    memberQuery,
    memberResults,
    memberSearchLoading,
    selectedMember,
    jobs,
    jobsLoading,
    jobsError,
    selectedJobId,
    preview,
    memberSorLoading,
    onSearchMembers,
    onSelectMember,
    onSelectJob,
    onLoadMemberSor,
    onCopyText
}) {
    const selectedJob = (jobs || []).find(job => job?.id === selectedJobId) || null;

    const renderTableArea = () => {
        if (!selectedMember) {
            return (
                <EmptyState
                    showIcon
                    icon={<FileSearchOutlined className={styles.emptyIcon} />}
                    title="No member selected"
                    description="Search for a member above to browse their uploaded documents."
                />
            );
        }
        if (jobsError) {
            return (
                <Card className={styles.errorCard}>
                    <WarningOutlined className={styles.errorIcon} />
                    <div className={styles.errorBody}>
                        <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.errorTitle}>
                            {jobsError.title}
                        </Text>
                        <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.errorDetail}>
                            {jobsError.detail}
                        </Text>
                    </div>
                </Card>
            );
        }
        if (!jobsLoading && !(jobs || []).length) {
            return (
                <EmptyState
                    showIcon
                    icon={<InboxOutlined className={styles.emptyIcon} />}
                    title="No uploads yet"
                    description="This member has not uploaded any documents through the manual intake pipeline."
                />
            );
        }
        return <IntakeJobsTable jobs={jobs} loading={jobsLoading} selectedJobId={selectedJobId} onSelectJob={onSelectJob} />;
    };

    const renderRail = () => {
        if (!selectedMember) {
            return (
                <EmptyState
                    showIcon
                    icon={<SelectOutlined className={styles.emptyIcon} />}
                    title="Nothing to preview"
                    description="Pick a member, then click an upload row to inspect its stored bytes and metadata."
                />
            );
        }
        return (
            <>
                <Card className={styles.memberCard}>
                    <div className={styles.memberCardBody}>
                        <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.memberName}>
                            {selectedMember.name || "Unnamed member"}
                        </Text>
                        <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.memberMeta}>
                            {selectedMember.email || "—"} · <span className={styles.mono}>{selectedMember.clientId}</span>
                        </Text>
                    </div>
                    <Button variant={ButtonVariants.DEFAULT} color={ButtonColors.SECONDARY} className={styles.loadSorButton} loading={memberSorLoading} onClick={onLoadMemberSor}>
                        <ImportOutlined />
                        Load this member's SOR into the composer
                    </Button>
                </Card>
                {selectedJob ? (
                    <>
                        <DocumentPreview job={selectedJob} preview={preview} />
                        <JobMetadataCard job={selectedJob} onCopyText={onCopyText} />
                    </>
                ) : (
                    <EmptyState
                        showIcon
                        icon={<SelectOutlined className={styles.emptyIcon} />}
                        title="No upload selected"
                        description="Click a row in the uploads table to preview the stored document and its metadata."
                    />
                )}
            </>
        );
    };

    return (
        <SplitLayout key="uploads-simulator-audit" embedded className={styles.splitRoot} contentClassName={styles.splitContent} sideClassName={styles.splitSide} side={renderRail()}>
            <MemberSearch
                query={memberQuery}
                results={memberResults}
                loading={memberSearchLoading}
                selectedClientId={selectedMember?.clientId || null}
                busy={memberSorLoading}
                onSearch={onSearchMembers}
                onSelect={onSelectMember}
            />
            {renderTableArea()}
        </SplitLayout>
    );
}

export default DocumentAuditPanel;
